import type { Edge, Node } from '@xyflow/react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import * as Api from '@ViewModels';
import { useAutomationTemplateAddStepMutation } from '../../../../queries/Automation/mutations/steps/useAutomationTemplateAddStepMutation';
import { useAutomationTemplateRemoveStepMutation } from '../../../../queries/Automation/mutations/steps/useAutomationTemplateRemoveStepMutation';
import { useAutomationTemplateReorderStepsMutation } from '../../../../queries/Automation/mutations/steps/useAutomationTemplateReorderStepsMutation';
import {
	useAutomationTemplateStepSwitchAddCaseMutation,
	useAutomationTemplateStepSwitchRemoveCaseMutation,
	useAutomationTemplateStepSwitchReorderCasesMutation,
	useAutomationTemplateStepSwitchUpdateCaseMutation,
} from '../../../../queries/Automation/mutations/switchCases';
import { useDeleteAutomationTemplateMutation } from '../../../../queries/Automation/mutations/template';
import { useRemoveBehaviorCaseMutation } from '../../../../queries/Automation/mutations/behaviorSwitch';
import { NODE_TYPES, type BaseNodeData } from '../../../../web/containers/AutomationFlows/types';
import type { ChainTerminus } from '../../../../web/containers/AutomationFlows/hooks/useChainTermini';
import type { CrossSegmentMoveParams } from '../../../../web/containers/AutomationFlows/types/canvasProps';
import type { DeletedStepInfo } from '../../../../web/containers/AutomationFlows/utils/nodeDelete';
import {
	nodeTypeToStepType,
	createDefaultStepData,
} from '../../../../web/containers/AutomationFlows/utils/stepTypeMapping';
import { MAX_BRANCH_NESTING_DEPTH } from '../../../../web/containers/AutomationFlows/utils/nestingLimits';
import { normalizeBehaviorBranchSchedule } from '../../../../web/containers/AutomationFlows/utils/behaviorBranchSchedule';
import { isPolicyBasedSegmentTrigger } from '../../../../web/containers/AutomationFlows/utils/segmentCriteria';
import { findSwitchNode } from '../../../../web/containers/AutomationFlows/utils/branchNodes';
import { isAnySwitchStep } from '../../../../models/Automations';

// ---------------------------------------------------------------------------
// Shared helper
// ---------------------------------------------------------------------------

/**
 * Resolve the real step ID from a switch node.
 * Tries the matched node's `data.stepId` first, then falls back to parsing the
 * node ID pattern `step-{id}`.
 */
function resolveSwitchStepId(
	switchNodeId: string,
	nodesRef: React.MutableRefObject<Node<BaseNodeData>[]>
): string | null {
	const switchNode = findSwitchNode(nodesRef.current, switchNodeId);
	if (switchNode?.data && 'stepId' in switchNode.data) {
		const stepId = (switchNode.data as { stepId?: string }).stepId;
		if (stepId) {
			return stepId;
		}
	}
	const match = switchNodeId.match(/^step-(.+)$/);
	return match ? match[1] : null;
}

function sanitizeStepForCreate(step: Api.IAutomationStep): Api.IAutomationStep {
	const {
		id: _id,
		templateVersionId: _templateVersionId,
		communicationStats: _stats,
		...rest
	} = step as unknown as Record<string, unknown>;
	return rest as Api.IAutomationStep;
}

/**
 * Resolves the owner template ID for a switch node.
 *
 * Nested switch nodes belong to child templates. Using the root template ID
 * against nested switch mutations causes backend "step not found" errors.
 */
function resolveSwitchOwnerTemplateId(
	switchNodeId: string,
	nodesRef: React.MutableRefObject<Node<BaseNodeData>[]>,
	fallbackTemplateId: string
): string {
	const switchNode = findSwitchNode(nodesRef.current, switchNodeId);
	if (switchNode?.data && 'automationTemplate' in switchNode.data) {
		const ownerTemplate = (switchNode.data as { automationTemplate?: Api.IAutomationTemplate }).automationTemplate;
		if (ownerTemplate?.id) {
			return ownerTemplate.id;
		}
	}

	return fallbackTemplateId;
}

function isBehaviorSwitchStep(step: { _type?: unknown } | undefined): boolean {
	return step?._type === 'BehaviorSwitchAutomationStep';
}

function isBehaviorDefaultCase(caseStatement: {
	_type?: unknown;
	caseType?: unknown;
	isDefault?: unknown;
	isTimeout?: unknown;
}): boolean {
	const modelType = typeof caseStatement._type === 'string' ? caseStatement._type.toLowerCase() : '';
	const caseType = typeof caseStatement.caseType === 'string' ? caseStatement.caseType.toLowerCase() : '';

	return (
		caseStatement.isDefault === true ||
		caseStatement.isTimeout === true ||
		modelType.includes('timeoutcasestatement') ||
		modelType.includes('defaultcasestatement') ||
		caseType.includes('timeoutcasestatement') ||
		caseType.includes('defaultcasestatement')
	);
}

function buildDefaultSegmentFilter(trigger?: Api.IAutomationTrigger): Api.IContactFilterCriteria {
	if (trigger?._type === Api.AutomationTriggerType.OpportunityCreated) {
		return {
			criteria: [],
			op: Api.FilterOperator.And,
		};
	}

	return {
		criteria: [
			{
				property: isPolicyBasedSegmentTrigger(trigger)
					? Api.ContactFilterCriteriaProperty.Policy
					: Api.ContactFilterCriteriaProperty.Tag,
			},
		],
	};
}

function isAnySwitchNodeId(
	switchNodeId: string | undefined,
	nodesRef: React.MutableRefObject<Node<BaseNodeData>[]>
): boolean {
	if (!switchNodeId) {
		return false;
	}

	const switchNode = findSwitchNode(nodesRef.current, switchNodeId);
	const automationStep = (switchNode?.data as { automationStep?: { _type?: unknown } } | undefined)?.automationStep;
	return isAnySwitchStep(automationStep);
}

// Detects whether a paste terminus lives inside a switch branch (regular Switch OR
// BehaviorSwitch). Both publish as child templates and inherit the root anchoring rule,
// so both need the same schedule normalization on paste.
function isSwitchBranchTerminus(
	terminus: ChainTerminus | null,
	nodesRef: React.MutableRefObject<Node<BaseNodeData>[]>,
	edgesRef?: React.MutableRefObject<Edge[]>
): boolean {
	if (!terminus) {
		return false;
	}

	if (terminus.node.type === NODE_TYPES.BEHAVIOR_CASE || terminus.node.type === NODE_TYPES.SEGMENT) {
		return true;
	}

	const nodeData = terminus.node.data as {
		parentCaseInfo?: { isBehaviorCase?: boolean; parentSwitchNodeId?: string };
		parentSwitchNodeId?: string;
	};
	if (nodeData.parentCaseInfo != null) {
		return true;
	}

	if (
		isAnySwitchNodeId(nodeData.parentSwitchNodeId, nodesRef) ||
		isAnySwitchNodeId(nodeData.parentCaseInfo?.parentSwitchNodeId, nodesRef) ||
		isAnySwitchNodeId(terminus.branchInfo?.parentSwitchId, nodesRef)
	) {
		return true;
	}

	// Fallback: parentCaseInfo may not propagate to deeply-nested optimistic children.
	// Walk back through edges to see if any ancestor is a switch step (regular or behavior).
	const edges = edgesRef?.current;
	if (!edges || edges.length === 0) {
		return false;
	}
	const visited = new Set<string>();
	let cursor: string | undefined = terminus.node.id;
	while (cursor && !visited.has(cursor)) {
		visited.add(cursor);
		const parentEdge = edges.find(edge => edge.target === cursor);
		const parentId = parentEdge?.source;
		if (!parentId) {
			break;
		}
		if (isAnySwitchNodeId(parentId, nodesRef)) {
			return true;
		}
		cursor = parentId;
	}

	return false;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseStepMutationsProps {
	templateId: string;
	template: Api.IAutomationTemplate | undefined;
	impersonationContext?: Api.IImpersonationContext;
	nodesRef: React.MutableRefObject<Node<BaseNodeData>[]>;
	edgesRef?: React.MutableRefObject<Edge[]>;
	refetch: () => void;
	onDeleteSuccess: () => void;
	setCanvasSyncVersion: React.Dispatch<React.SetStateAction<number>>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useStepMutations - Wires up all mutation hooks and exposes action handlers.
 *
 * Extracted from AutomationCanvasEditor to give the god-component a single
 * entry point for every write operation (add step, add segment, reorder, etc.).
 */
export function useStepMutations({
	templateId,
	template,
	impersonationContext,
	nodesRef,
	edgesRef,
	refetch,
	onDeleteSuccess,
	setCanvasSyncVersion,
}: UseStepMutationsProps) {
	// -- Wire up mutations --

	const deleteTemplateMutation = useDeleteAutomationTemplateMutation({
		onSuccess: () => {
			toast.success('Automation deleted successfully');
			onDeleteSuccess();
		},
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to delete automation');
		},
	});

	const addStepMutation = useAutomationTemplateAddStepMutation({
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to add step');
		},
	});

	const addCaseMutation = useAutomationTemplateStepSwitchAddCaseMutation({
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to add segment');
		},
	});

	const removeCaseMutation = useAutomationTemplateStepSwitchRemoveCaseMutation({
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to delete segment');
		},
	});

	const removeBehaviorCaseMutation = useRemoveBehaviorCaseMutation({
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to delete behavior branch');
		},
	});

	const removeStepMutation = useAutomationTemplateRemoveStepMutation({
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to remove step');
		},
	});

	const reorderStepsMutation = useAutomationTemplateReorderStepsMutation({
		onSuccess: () => {
			refetch();
		},
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to reorder steps');
			// Resync canvas with server state so the optimistic update is reverted.
			refetch();
			setCanvasSyncVersion(prev => prev + 1);
		},
	});

	const reorderCasesMutation = useAutomationTemplateStepSwitchReorderCasesMutation({
		onSuccess: () => {
			refetch();
		},
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to reorder segments');
		},
	});

	const updateCaseMutation = useAutomationTemplateStepSwitchUpdateCaseMutation({
		onError: apiError => {
			toast.error(apiError?.systemMessage || 'Failed to update segment criteria');
		},
	});

	// -- Delete --

	const handleDelete = useCallback(async () => {
		if (!templateId) {
			return;
		}
		await deleteTemplateMutation.mutateAsync({ id: templateId, impersonationContext });
	}, [templateId, deleteTemplateMutation, impersonationContext]);

	// -- Delete steps from canvas (right-click or keyboard delete) --

	const handleDeleteSteps = useCallback(
		async (steps: DeletedStepInfo[]) => {
			if (steps.length === 0) {
				return;
			}

			try {
				// Delete steps sequentially to avoid race conditions on the same template.
				for (const step of steps) {
					const targetTemplateId = step.ownerTemplateId || templateId;
					if (!targetTemplateId) {
						continue;
					}

					await removeStepMutation.mutateAsync({
						templateId: targetTemplateId,
						stepType: step.stepType,
						stepId: step.stepId,
						impersonationContext,
						relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
					});
				}

				refetch();
			} catch {
				toast.error('Failed to delete step(s) from server');
			}
		},
		[templateId, removeStepMutation, refetch, impersonationContext]
	);

	// -- Duplicate step --

	const handleDuplicateStep = useCallback(
		async ({
			templateId: targetTemplateId,
			stepType,
			step,
			beforeId,
		}: {
			templateId: string;
			stepType: Api.AutomationStepType;
			step: Api.IAutomationStep;
			beforeId?: string;
		}) => {
			if (!targetTemplateId) {
				return;
			}

			try {
				if (stepType === Api.AutomationStepType.Switch) {
					toast.info('Duplicating conditional steps is not supported yet');
					return;
				}

				const cleanStep = sanitizeStepForCreate(step);
				await addStepMutation.mutateAsync({
					templateId: targetTemplateId,
					stepType,
					step: cleanStep,
					beforeId,
					impersonationContext,
					relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
				});

				refetch();
				setCanvasSyncVersion(prev => prev + 1);
			} catch {
				toast.error('Failed to duplicate step');
			}
		},
		[addStepMutation, impersonationContext, refetch, setCanvasSyncVersion, templateId]
	);

	// -- Paste steps at a chain terminus --

	const handlePasteSteps = useCallback(
		async (
			terminus: ChainTerminus,
			steps: { stepType: Api.AutomationStepType; step: Api.IAutomationStep }[],
			targetTemplateId: string
		) => {
			if (!templateId) {
				toast.error('No template ID available');
				return;
			}
			if (steps.length === 0) {
				return;
			}

			const trigger = template?.draft?.trigger || template?.published?.trigger;
			const shouldNormalizeBehaviorBranchSchedule = isSwitchBranchTerminus(terminus, nodesRef, edgesRef);

			try {
				for (const { stepType, step } of steps) {
					if (stepType === Api.AutomationStepType.Switch) {
						toast.info('Pasting conditional steps is not supported yet');
						continue;
					}

					const cleanStep = sanitizeStepForCreate(step);
					if (shouldNormalizeBehaviorBranchSchedule) {
						cleanStep.schedule = normalizeBehaviorBranchSchedule(cleanStep.schedule, trigger) ?? undefined;
					}
					await addStepMutation.mutateAsync({
						templateId: targetTemplateId,
						stepType,
						step: cleanStep,
						impersonationContext,
						relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
					});
				}

				refetch();
				setCanvasSyncVersion(prev => prev + 1);
			} catch {
				toast.error('Failed to paste step(s)');
			}
		},
		[templateId, template, addStepMutation, impersonationContext, nodesRef, edgesRef, refetch, setCanvasSyncVersion]
	);

	// -- Add step to chain --

	const handleAddStepToChain = useCallback(
		async (terminus: ChainTerminus | null, nodeType: string, targetTemplateId: string) => {
			if (!templateId) {
				toast.error('No template ID available');
				return null;
			}

			const stepType = nodeTypeToStepType(nodeType);
			if (!stepType) {
				toast.error('Invalid step type');
				return null;
			}

			if (stepType === Api.AutomationStepType.Switch && (terminus?.nestingDepth || 0) >= MAX_BRANCH_NESTING_DEPTH) {
				toast.error(`Conditional nesting is limited to ${MAX_BRANCH_NESTING_DEPTH} levels.`);
				return null;
			}

			const trigger = template?.draft?.trigger || template?.published?.trigger;
			const stepData = createDefaultStepData(stepType, trigger) as Api.IAutomationStep;
			if (stepType === Api.AutomationStepType.NoAction) {
				stepData.name = 'Do nothing';
				// Normalize unconditionally — applies to both top-level and inside-segment Do Nothings.
				// This is intentional:
				//   - AutomationTemplatePublisher.cs:295-326 rejects any non-RelativeToAnchor top-level
				//     step under strictly-anchored roots (Birthday/Renewal/etc.). So a persisted
				//     Immediately would fail publish anyway with "All steps must have the scheduled as anchored".
				//   - For non-anchored roots and Meeting/EventRegistration, the normalizer is a no-op
				//     (preserves Immediately). Non-anchored automations are unaffected.
				// Net effect: a top-level Do Nothing under Birthday persists as {RelativeToAnchor, days=0}
				// ("on the birthday") instead of failing publish with an unhelpful API error.
				stepData.schedule = normalizeBehaviorBranchSchedule(
					{ criteria: Api.AutomationStepScheduleCriteria.Immediately },
					trigger,
					Api.AutomationStepType.NoAction
				) ?? { criteria: Api.AutomationStepScheduleCriteria.Immediately };
			}
			if (stepType === Api.AutomationStepType.Switch) {
				const switchStepData = {
					...stepData,
					cases: [] as Api.IAutomationStepCaseStatement[],
				};

				const createdStep = await addStepMutation.mutateAsync({
					templateId: targetTemplateId,
					stepType,
					step: switchStepData as Api.IAutomationStep,
					impersonationContext,
					relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
				});

				await new Promise(resolve => setTimeout(resolve, 100));

				let persistedSwitchStep = createdStep as Api.ISwitchAutomationStep;
				const addCaseWithRetry = async (isDefault: boolean, retries = 1): Promise<Api.ISwitchAutomationStep> => {
					const caseData: Api.IAutomationStepCaseStatement = {
						filter: isDefault ? null : buildDefaultSegmentFilter(trigger),
						isDefault,
					};

					try {
						persistedSwitchStep = await addCaseMutation.mutateAsync({
							templateId: targetTemplateId,
							stepId: createdStep.id,
							caseData,
							impersonationContext,
							relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
						});
						return persistedSwitchStep;
					} catch (caughtError: unknown) {
						const caughtApiError = caughtError as { status?: number };
						if (retries > 0 && caughtApiError?.status === 405) {
							await new Promise(resolve => setTimeout(resolve, 500));
							return addCaseWithRetry(isDefault, retries - 1);
						}
						throw caughtError;
					}
				};

				await addCaseWithRetry(false);
				persistedSwitchStep = await addCaseWithRetry(true);

				const defaultCase = persistedSwitchStep.cases?.find(caseStatement => caseStatement.isDefault);
				if (defaultCase?.automationTemplateId) {
					// Normalize the seed schedule against the root trigger so the persisted criteria
					// passes the API publisher's anchoring rule (lines 295-326 / 514) on first publish.
					const defaultDoNothingSchedule = normalizeBehaviorBranchSchedule(
						{ criteria: Api.AutomationStepScheduleCriteria.Immediately },
						trigger,
						Api.AutomationStepType.NoAction
					) ?? { criteria: Api.AutomationStepScheduleCriteria.Immediately };
					const defaultDoNothingStep: Api.INoActionAutomationStep = {
						_type: 'NoActionAutomationStep',
						name: 'Do nothing',
						schedule: defaultDoNothingSchedule,
					};
					// The switch + cases are already persisted; if seeding the default branch's
					// Do Nothing fails we still want to refetch so the canvas reconciles, rather
					// than leaving the user with an optimistic node and no success feedback.
					try {
						await addStepMutation.mutateAsync({
							templateId: defaultCase.automationTemplateId,
							stepType: Api.AutomationStepType.NoAction,
							step: defaultDoNothingStep,
							impersonationContext,
							relatedTemplateIds: defaultCase.automationTemplateId !== templateId ? [templateId] : undefined,
						});
					} catch (seedError) {
						console.warn('Failed to seed default Do nothing step', seedError);
					}
				}

				refetch();
				toast.success('Step added successfully');
				return persistedSwitchStep;
			}

			const newStep = await addStepMutation.mutateAsync({
				templateId: targetTemplateId,
				stepType,
				step: stepData,
				impersonationContext,
				relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
			});

			// Replace-on-add for the default "everyone else" branch: it is auto-seeded with a
			// terminal "Do nothing" step. Once the real action is created in the branch template,
			// remove the seed so the branch resolves to the chosen action instead. Add-first (not
			// delete-first) keeps the default branch from ever being momentarily empty; a failed
			// removal only leaves a stray Do nothing the user can delete, never a lost action.
			let seedRemovalFailed = false;
			if (terminus?.isDefaultBranchDoNothing) {
				const doNothingStepId = (terminus.node.data as { stepId?: string }).stepId;
				if (doNothingStepId) {
					try {
						await removeStepMutation.mutateAsync({
							templateId: targetTemplateId,
							stepType: Api.AutomationStepType.NoAction,
							stepId: doNothingStepId,
							impersonationContext,
							relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
						});
					} catch (removeError) {
						seedRemovalFailed = true;
						console.warn(
							'Failed to remove seeded Do nothing step after adding replacement',
							removeError instanceof Error ? removeError.message : String(removeError)
						);
					}
				} else {
					// Seed not yet reconciled with a server ID (rare: user acted mid-persist), so it
					// can't be removed. The optimistic canvas left it in place to match this.
					seedRemovalFailed = true;
				}
			}

			refetch();
			if (seedRemovalFailed) {
				toast.warning('Step added. Delete the leftover "Do nothing" step to finish setting up this branch.');
			} else {
				toast.success('Step added successfully');
			}
			return newStep;
		},
		[templateId, template, addStepMutation, addCaseMutation, removeStepMutation, refetch, impersonationContext]
	);

	// -- Add segment to switch step --

	const handleAddSegment = useCallback(
		async (switchNodeId: string) => {
			if (!templateId) {
				toast.error('No template ID available');
				return null;
			}

			const stepId = resolveSwitchStepId(switchNodeId, nodesRef);

			if (!stepId) {
				toast.error('Switch step ID not found - step may not be saved yet');
				return null;
			}

			const ownerTemplateId = resolveSwitchOwnerTemplateId(switchNodeId, nodesRef, templateId);
			const trigger = template?.draft?.trigger || template?.published?.trigger;
			const defaultFilter = buildDefaultSegmentFilter(trigger);

			const persistedSwitchStep = await addCaseMutation.mutateAsync({
				templateId: ownerTemplateId,
				stepId,
				caseData: {
					filter: defaultFilter,
					isDefault: false,
				},
				impersonationContext,
				relatedTemplateIds: ownerTemplateId !== templateId ? [templateId] : undefined,
			});

			// Refetch to get canonical server state. Do NOT bump setCanvasSyncVersion here:
			// the optimistic update (applyPersistedSwitchCaseGraphUpdate) has already positioned
			// the new nodes correctly. useCanvasSync Effect 3 will detect the structural change
			// from the refetch and merge new data while preserving positions and measurements.
			// Bumping syncVersion would force-reset the entire canvas, erasing the optimistic
			// positions and causing visible flicker as the layout re-runs from scratch.
			void refetch();

			toast.success('Segment added successfully');
			return persistedSwitchStep;
		},
		[templateId, template, addCaseMutation, refetch, impersonationContext, nodesRef]
	);

	// -- Delete branch case(s) --

	const handleDeleteBranchCases = useCallback(
		async (casesToDelete: { switchNodeId: string; caseId: string }[]) => {
			if (!templateId || casesToDelete.length === 0) {
				return;
			}

			const toastId = toast.loading(
				`Deleting ${casesToDelete.length} branch${casesToDelete.length === 1 ? '' : 'es'}...`
			);

			try {
				// Group deletions by switch node.
				const casesBySwitch = new Map<string, string[]>();
				for (const item of casesToDelete) {
					const list = casesBySwitch.get(item.switchNodeId) ?? [];
					list.push(item.caseId);
					casesBySwitch.set(item.switchNodeId, list);
				}

				for (const [switchNodeId, caseIds] of casesBySwitch.entries()) {
					const stepId = resolveSwitchStepId(switchNodeId, nodesRef);
					if (!stepId) {
						continue;
					}

					// Prefer the template ID stored on the switch node itself.
					let ownerTemplateId = templateId;
					const switchNode = findSwitchNode(nodesRef.current, switchNodeId);
					if (switchNode?.data && 'automationTemplate' in switchNode.data) {
						const storedTemplate = (switchNode.data as { automationTemplate?: Api.IAutomationTemplate })
							.automationTemplate;
						if (storedTemplate?.id) {
							ownerTemplateId = storedTemplate.id;
						}
					}

					const switchStep = (switchNode?.data as { automationStep?: Api.IAutomationStep } | undefined)?.automationStep;
					const isBehaviorSwitch = isBehaviorSwitchStep(switchStep as { _type?: unknown } | undefined);
					const existingCases =
						(
							switchStep as
								| {
										cases?: { id?: string; isDefault?: boolean; caseType?: string; _type?: string }[];
								  }
								| undefined
						)?.cases ?? [];
					const nonDefaultCaseIds = existingCases
						.filter(caseStatement =>
							isBehaviorSwitch ? !isBehaviorDefaultCase(caseStatement) : !caseStatement.isDefault
						)
						.map(caseStatement => caseStatement.id)
						.filter((id): id is string => !!id);

					const shouldDeleteEntireSwitch =
						nonDefaultCaseIds.length > 0 && nonDefaultCaseIds.every(caseId => caseIds.includes(caseId));

					if (shouldDeleteEntireSwitch) {
						await removeStepMutation.mutateAsync({
							templateId: ownerTemplateId,
							stepType: Api.AutomationStepType.Switch,
							stepId,
							impersonationContext,
							relatedTemplateIds: ownerTemplateId !== templateId ? [templateId] : undefined,
						});
						continue;
					}

					for (const caseId of caseIds) {
						if (isBehaviorSwitch) {
							await removeBehaviorCaseMutation.mutateAsync({
								templateId: ownerTemplateId,
								stepId,
								caseId,
								impersonationContext,
								relatedTemplateIds: ownerTemplateId !== templateId ? [templateId] : undefined,
							});
							continue;
						}

						await removeCaseMutation.mutateAsync({
							templateId: ownerTemplateId,
							stepId,
							caseId,
							impersonationContext,
							relatedTemplateIds: ownerTemplateId !== templateId ? [templateId] : undefined,
						});
					}
				}

				// Keep the optimistic canvas deletion in place.
				// Forcing a canvas sync here can briefly restore the stale pre-delete graph
				// before the refetch completes, which makes segment deletion look non-live.
				await Promise.resolve(refetch());
				toast.success('Branch deleted', { id: toastId });
			} catch {
				// Roll back the optimistic deletion if the server mutation fails.
				await Promise.resolve(refetch());
				setCanvasSyncVersion(prev => prev + 1);
				toast.error('Failed to delete branch(es)', { id: toastId });
			}
		},
		[
			templateId,
			nodesRef,
			removeBehaviorCaseMutation,
			removeCaseMutation,
			removeStepMutation,
			impersonationContext,
			refetch,
			setCanvasSyncVersion,
		]
	);

	// -- Reorder steps --

	const handleReorderSteps = useCallback(
		async (orderedStepIds: string[], targetTemplateId: string) => {
			if (!targetTemplateId) {
				return;
			}
			try {
				await reorderStepsMutation.mutateAsync({
					templateId: targetTemplateId,
					stepIds: orderedStepIds,
					impersonationContext,
					relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
				});
			} catch {
				// Error already handled by onError callback — catch here to
				// prevent an unhandled promise rejection (this function is called
				// fire-and-forget from useReordering).
			}
		},
		[reorderStepsMutation, impersonationContext, templateId]
	);

	// -- Reorder segments (cases) within a switch step --

	const handleReorderSegments = useCallback(
		async (caseIds: string[], targetTemplateId: string, stepId: string) => {
			if (!targetTemplateId || !stepId) {
				return;
			}
			await reorderCasesMutation.mutateAsync({
				templateId: targetTemplateId,
				stepId,
				caseIds,
				impersonationContext,
				relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
			});
		},
		[reorderCasesMutation, impersonationContext, templateId]
	);

	// -- Update segment case criteria --

	const handleUpdateCaseCriteria = useCallback(
		(
			switchNodeId: string,
			caseId: string,
			filter: Api.IContactFilterCriteria | undefined,
			stageIds?: string[],
			prompt?: string
		) => {
			if (!templateId) {
				return;
			}

			const stepId = resolveSwitchStepId(switchNodeId, nodesRef);
			if (!stepId) {
				return;
			}

			const ownerTemplateId = resolveSwitchOwnerTemplateId(switchNodeId, nodesRef, templateId);

			// Find the existing case statement to preserve non-filter fields
			let existingCase: Api.IAutomationStepCaseStatement | undefined;
			const switchNode = findSwitchNode(nodesRef.current, switchNodeId);
			if (switchNode?.data && 'automationStep' in switchNode.data) {
				const switchStep = switchNode.data.automationStep as Api.ISwitchAutomationStep | undefined;
				existingCase = switchStep?.cases?.find(c => c.id === caseId);
			}

			// When the caller passes stageIds explicitly, write it alongside filter in one PUT —
			// avoids the optimistic case re-introducing pre-clear stageIds during a mode switch.
			const nextStageIds = stageIds ?? existingCase?.stageIds ?? [];
			const nextFilter = filter || null;
			const nextPrompt = (prompt ?? existingCase?.prompt ?? '').trim();
			const caseData: Api.IAutomationStepCaseStatement = {
				...existingCase,
				id: caseId,
				filter: nextFilter,
				stageIds: nextStageIds,
				prompt: nextPrompt || null,
				// Mirrors server rule: case is default when filter, stageIds and prompt are all empty.
				isDefault: !nextFilter && nextStageIds.length === 0 && !nextPrompt,
			};

			updateCaseMutation.mutate({
				templateId: ownerTemplateId,
				stepId,
				caseId,
				caseData,
				impersonationContext,
				relatedTemplateIds: ownerTemplateId !== templateId ? [templateId] : undefined,
			});
		},
		[templateId, updateCaseMutation, impersonationContext, nodesRef]
	);

	// -- Update segment case name --

	const handleUpdateCaseName = useCallback(
		(switchNodeId: string, caseId: string, name: string) => {
			if (!templateId) {
				return;
			}

			const stepId = resolveSwitchStepId(switchNodeId, nodesRef);
			if (!stepId) {
				return;
			}

			const ownerTemplateId = resolveSwitchOwnerTemplateId(switchNodeId, nodesRef, templateId);

			// Find the existing case statement to preserve all fields
			let existingCase: Api.IAutomationStepCaseStatement | undefined;
			const switchNode = findSwitchNode(nodesRef.current, switchNodeId);
			if (switchNode?.data && 'automationStep' in switchNode.data) {
				const switchStep = switchNode.data.automationStep as Api.ISwitchAutomationStep | undefined;
				existingCase = switchStep?.cases?.find(c => c.id === caseId);
			}

			const caseData: Api.IAutomationStepCaseStatement = {
				...existingCase,
				id: caseId,
				name,
			};

			updateCaseMutation.mutate({
				templateId: ownerTemplateId,
				stepId,
				caseId,
				caseData,
				impersonationContext,
				relatedTemplateIds: ownerTemplateId !== templateId ? [templateId] : undefined,
			});
		},
		[templateId, updateCaseMutation, impersonationContext, nodesRef]
	);

	// -- Update segment case AI prompt --

	const handleUpdateCasePrompt = useCallback(
		(
			switchNodeId: string,
			caseId: string,
			prompt: string,
			filter: Api.IContactFilterCriteria | undefined,
			stageIds: string[]
		) => {
			if (!templateId) {
				return;
			}

			const stepId = resolveSwitchStepId(switchNodeId, nodesRef);
			if (!stepId) {
				return;
			}

			const ownerTemplateId = resolveSwitchOwnerTemplateId(switchNodeId, nodesRef, templateId);

			let existingCase: Api.IAutomationStepCaseStatement | undefined;
			const switchNode = findSwitchNode(switchNodeId, nodesRef);
			if (switchNode?.data && 'automationStep' in switchNode.data) {
				const switchStep = switchNode.data.automationStep as Api.ISwitchAutomationStep | undefined;
				existingCase = switchStep?.cases?.find(c => c.id === caseId);
			}

			const nextPrompt = prompt.trim();
			const nextFilter = filter ?? null;
			const caseData: Api.IAutomationStepCaseStatement = {
				...existingCase,
				id: caseId,
				filter: nextFilter,
				stageIds,
				prompt: nextPrompt || null,
				// Mirrors server rule: case is default when filter, stageIds and prompt are all empty.
				isDefault: !nextFilter && stageIds.length === 0 && !nextPrompt,
			};

			updateCaseMutation.mutate({
				templateId: ownerTemplateId,
				stepId,
				caseId,
				caseData,
				impersonationContext,
				relatedTemplateIds: ownerTemplateId !== templateId ? [templateId] : undefined,
			});
		},
		[templateId, updateCaseMutation, impersonationContext, nodesRef]
	);

	// -- Update segment case stage gating (OpportunityCreated triggers) --

	const handleUpdateCaseStageIds = useCallback(
		(
			switchNodeId: string,
			caseId: string,
			stageIds: string[],
			filter?: Api.IContactFilterCriteria,
			prompt?: string
		) => {
			if (!templateId) {
				return;
			}

			const stepId = resolveSwitchStepId(switchNodeId, nodesRef);
			if (!stepId) {
				return;
			}

			const ownerTemplateId = resolveSwitchOwnerTemplateId(switchNodeId, nodesRef, templateId);

			// Preserve name from the optimistic case, but write `filter` and `stageIds` explicitly
			// from the caller's current segment state — the cached `existingCase.filter` may still
			// hold the pre-clear value while the criteria-clear PUT is in flight.
			let existingCase: Api.IAutomationStepCaseStatement | undefined;
			const switchNode = findSwitchNode(nodesRef.current, switchNodeId);
			if (switchNode?.data && 'automationStep' in switchNode.data) {
				const switchStep = switchNode.data.automationStep as Api.ISwitchAutomationStep | undefined;
				existingCase = switchStep?.cases?.find(c => c.id === caseId);
			}

			const nextPrompt = (prompt ?? existingCase?.prompt ?? '').trim();
			const caseData: Api.IAutomationStepCaseStatement = {
				id: caseId,
				name: existingCase?.name,
				automationTemplateId: existingCase?.automationTemplateId,
				prompt: nextPrompt || null,
				filter: filter ?? null,
				stageIds,
				// Mirrors server rule: case is default when filter, stageIds and prompt are all empty.
				isDefault: !filter && stageIds.length === 0 && !nextPrompt,
			} as Api.IAutomationStepCaseStatement;

			updateCaseMutation.mutate({
				templateId: ownerTemplateId,
				stepId,
				caseId,
				caseData,
				impersonationContext,
				relatedTemplateIds: ownerTemplateId !== templateId ? [templateId] : undefined,
			});
		},
		[templateId, updateCaseMutation, impersonationContext, nodesRef]
	);

	// -- Move steps between segments (cross-segment move) --

	const handleMoveStepsCrossSegment = useCallback(
		async (params: CrossSegmentMoveParams) => {
			const { steps, sourceTemplateId, targetTemplateId } = params;
			const toastId = toast.loading(`Moving ${steps.length} step(s) between segments...`);

			try {
				for (const step of steps) {
					await removeStepMutation.mutateAsync({
						templateId: sourceTemplateId,
						stepType: step.stepType,
						stepId: step.stepId,
						impersonationContext,
						relatedTemplateIds: sourceTemplateId !== templateId ? [templateId] : undefined,
					});
				}

				for (const step of steps) {
					const {
						id: _id,
						templateVersionId: _tvId,
						communicationStats: _stats,
						...cleanStep
					} = step.automationStep as Record<string, unknown>;
					await addStepMutation.mutateAsync({
						templateId: targetTemplateId,
						stepType: step.stepType,
						step: cleanStep as Api.IAutomationStep,
						impersonationContext,
						relatedTemplateIds: targetTemplateId !== templateId ? [templateId] : undefined,
					});
				}

				refetch();
				setCanvasSyncVersion(prev => prev + 1);
				toast.success(`Moved ${steps.length} step(s)`, { id: toastId });
			} catch {
				toast.error('Failed to move steps between segments', { id: toastId });
			}
		},
		[removeStepMutation, addStepMutation, refetch, impersonationContext, setCanvasSyncVersion, templateId]
	);

	return {
		isDeleting: deleteTemplateMutation.isPending,
		handleDelete,
		handleDeleteSteps,
		handleDeleteBranchCases,
		handleDuplicateStep,
		handlePasteSteps,
		handleAddStepToChain,
		handleAddSegment,
		handleReorderSteps,
		handleReorderSegments,
		handleUpdateCaseCriteria,
		handleUpdateCaseStageIds,
		handleUpdateCaseName,
		handleUpdateCasePrompt,
		handleMoveStepsCrossSegment,
	};
}
